import Papa from 'papaparse'
import { taxaPathKey } from 'Util/Visualisation/TaxaPathKey.ts'
import type { FilteredBucketValues } from '../Sinks/Selector/CubeFiltering.ts'
import type { TaxaAggregationResult } from '../Sinks/Selector/TaxaAggregation.ts'

export type SinkTableColumn = { key: string; label: string; numeric?: boolean }
export type SinkTableRow = Record<string, string | number | null>

/** The numbers behind one sink, in the shape the sink rendered them. */
export type SinkTable = {
  columns: SinkTableColumn[]
  rows: SinkTableRow[]
  /** The zoom path and filters these numbers were produced under. */
  caption?: string
}

export const GROUP_KEY = '__group'
export const SAMPLES_KEY = '__samples'
export const PARENT_KEY = '__parent'
export const TAXON_KEY = '__taxon'
export const NTAXA_KEY = '__ntaxa'
export const VALUE_KEY = '__value'
export const SHARE_KEY = '__share'

const SHARE_DECIMALS = 2

/**
 * One row per (group, category) — the numbers behind a multi-bucket chart, long rather than wide.
 *
 * Long form because it is the only shape the ticket's columns fit into: a wide bucket × taxon
 * matrix has nowhere to put a per-taxon n-taxa, and its column count is unbounded in the taxa.
 * A zero segment is dropped rather than written out, since the chart does not draw it either.
 *
 * `sampleCount` is nullable on purpose: `undefined` means no bucket stood behind the row, which is
 * not the same as a bucket that genuinely holds no samples. See [FilteredBucketValues].
 */
export function buildSeriesTable(opts: {
  parentTaxon?: string
  categories: string[]
  rows: Array<{ label: string; sampleCount?: number; values: Record<string, number> }>
  taxaCounts?: Record<string, number>
  groupLabel: string
  categoryLabel: string
  valueLabel: string
  caption?: string
}): SinkTable {
  const { parentTaxon, categories, rows, taxaCounts, groupLabel, categoryLabel, valueLabel, caption } = opts

  const columns: SinkTableColumn[] = []
  if (parentTaxon) columns.push({ key: PARENT_KEY, label: 'Parent taxon' })
  columns.push({ key: GROUP_KEY, label: groupLabel }, { key: SAMPLES_KEY, label: 'Samples', numeric: true })
  columns.push({ key: TAXON_KEY, label: categoryLabel })
  if (taxaCounts) columns.push({ key: NTAXA_KEY, label: 'n-taxa', numeric: true })
  columns.push({ key: VALUE_KEY, label: valueLabel, numeric: true }, { key: SHARE_KEY, label: 'Share %', numeric: true })

  const tableRows: SinkTableRow[] = []
  for (const row of rows) {
    const total = categories.reduce((sum, name) => sum + (row.values[name] ?? 0), 0)
    for (const name of categories) {
      const value = row.values[name] ?? 0
      if (value === 0) continue
      tableRows.push({
        ...(parentTaxon ? { [PARENT_KEY]: parentTaxon } : {}),
        [GROUP_KEY]: row.label,
        [SAMPLES_KEY]: row.sampleCount ?? null,
        [TAXON_KEY]: name,
        ...(taxaCounts ? { [NTAXA_KEY]: taxaCounts[name] ?? 0 } : {}),
        [VALUE_KEY]: value,
        [SHARE_KEY]: total > 0 ? Number(((value / total) * 100).toFixed(SHARE_DECIMALS)) : 0
      })
    }
  }

  return { columns, rows: tableRows, caption }
}

/**
 * One row per category in a single bucket — the ticket's worked example: the subordinate taxon, the
 * higher-level taxon it sits under, how many taxa it covers, and its magnitude.
 *
 * `taxaCounts` is optional because only a taxonomy breakdown has an n-taxa to report; a trait-group
 * composition has no subordinate taxa to count.
 */
export function buildBreakdownTable(opts: {
  parentTaxon?: string
  entries: Array<{ name: string; value: number }>
  taxaCounts?: Record<string, number>
  categoryLabel: string
  valueLabel: string
  caption?: string
}): SinkTable {
  const { parentTaxon, entries, taxaCounts, categoryLabel, valueLabel, caption } = opts

  const total = entries.reduce((sum, entry) => sum + entry.value, 0)

  const columns: SinkTableColumn[] = []
  if (parentTaxon) columns.push({ key: PARENT_KEY, label: 'Parent taxon' })
  columns.push({ key: TAXON_KEY, label: categoryLabel })
  if (taxaCounts) columns.push({ key: NTAXA_KEY, label: 'n-taxa', numeric: true })
  columns.push({ key: VALUE_KEY, label: valueLabel, numeric: true }, { key: SHARE_KEY, label: 'Share %', numeric: true })

  return {
    columns,
    rows: entries.map((entry) => ({
      ...(parentTaxon ? { [PARENT_KEY]: parentTaxon } : {}),
      [TAXON_KEY]: entry.name,
      ...(taxaCounts ? { [NTAXA_KEY]: taxaCounts[entry.name] ?? 0 } : {}),
      [VALUE_KEY]: entry.value,
      [SHARE_KEY]: total > 0 ? Number(((entry.value / total) * 100).toFixed(SHARE_DECIMALS)) : 0
    })),
    caption
  }
}

/** Flattens a single-bucket aggregation to the `{ name, value }` entries a breakdown table takes. */
export function breakdownEntries(result: TaxaAggregationResult): Array<{ name: string; value: number }> {
  const data = result.rows[0]?.data ?? {}
  return Object.entries(data)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
}

/**
 * How many distinct taxa sit under each category at `level` — the ticket's n-taxa.
 *
 * One cube value is one `(bucket, tax_path)`, so distinct paths under a category is the number of
 * taxa it covers. The category key is `taxa[level]` with the same `(n/a)` fallback the aggregation
 * uses, or the counts would not line up with the rows they annotate.
 */
export function countTaxaPerCategory(buckets: FilteredBucketValues[], level: number, bucketKey?: string): Record<string, number> {
  const scoped = bucketKey === undefined ? buckets : buckets.filter((bucket) => bucket.bucketKey === bucketKey)

  const seen: Record<string, Set<string>> = {}
  for (const bucket of scoped) {
    for (const value of bucket.values) {
      const category = value.taxa[level] ?? '(n/a)'
      ;(seen[category] ??= new Set()).add(taxaPathKey(value.taxa))
    }
  }

  return Object.fromEntries(Object.entries(seen).map(([category, paths]) => [category, paths.size]))
}

export function toCsv(table: SinkTable): string {
  return Papa.unparse(
    {
      fields: table.columns.map((column) => column.label),
      data: table.rows.map((row) => table.columns.map((column) => row[column.key] ?? ''))
    },
    { delimiter: ';' }
  )
}

/** Browsers cap `download` filenames at the OS's rules; strip what a filesystem would reject. */
export function csvFilename(title: string): string {
  // Trimming the substitute character matters: a title of only rejected characters collapses to
  // `_`, which is non-empty and would sail past a plain `|| 'data'`.
  const base = title.replace(/[^\w .-]+/g, '_').replace(/^[_\s]+|[_\s]+$/g, '')
  return `${base || 'data'}.csv`
}

export function downloadCsv(table: SinkTable, filename: string): void {
  // BOM so Excel reads it as UTF-8 — taxon names are full of accented and non-Latin characters.
  const blob = new Blob([`\uFEFF${toCsv(table)}`], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}
